import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Modal, ModalBody, ModalHeader, Button, Alert, Table } from 'reactstrap';
import Loader from 'components/Loader';
import __ from 'utils/i18n';
import { tenantId, sseBackendUrl } from 'config/envConfig';
import { uploadBulkUpdate } from '../../_shared/resources';
import { fetchResourceRP } from '../../../_shared/fetchResourceRP';
import { notification } from 'utils/notifications';
import { BackendRpErrorResponse } from '../../../_shared/BackendRpErrorResponse';

export const BulkUpdateProgressModal = ({ 
  showModal = false, 
  hideModal, 
  totalRows = 0,
  streamHubTopic = null,
  onSuccess,
  // Reload list without closing the modal (soft-complete / no SSE events)
  onRefresh = null,
  error = null,
  errorMessage: propErrorMessage = null,
  onGoBack = null,
  pendingUpload = false, // Nuevo: indica que aún no se hizo el upload
  file = null, // Nuevo: archivo a subir
  plan = null, // Nuevo: plan para el upload
}) => {
  const [progress, setProgress] = useState(0);
  const [, setCurrentRow] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [, setErrorMessage] = useState(null);
  const [, setIsUploadPending] = useState(pendingUpload);
  const [, setIsConnectionReady] = useState(false);
  const [updateErrors, setUpdateErrors] = useState([]); // Array de errores individuales por fila
  const [showErrorsModal, setShowErrorsModal] = useState(false); // Modal para mostrar errores
  const [completedWithoutProgress, setCompletedWithoutProgress] = useState(false);

  // Refs para mantener valores actualizados en closures
  const isUploadPendingRef = useRef(pendingUpload);
  const fileRef = useRef(file);
  const planRef = useRef(plan);

  // Obtener token de autenticación desde Redux
  const token = useSelector((state) => state.api?.token);
  const tokenRef = useRef(token);

  // Usar refs para evitar dependencias que cambian en cada render
  const hideModalRef = useRef(hideModal);
  const onSuccessRef = useRef(onSuccess);
  const onRefreshRef = useRef(onRefresh);

  useEffect(() => {
    hideModalRef.current = hideModal;
    onSuccessRef.current = onSuccess;
    onRefreshRef.current = onRefresh;
  }, [hideModal, onSuccess, onRefresh]);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // Sincronizar el estado isUploadPending con el prop pendingUpload
  useEffect(() => {
    if (pendingUpload !== undefined) {
      setIsUploadPending(pendingUpload);
      isUploadPendingRef.current = pendingUpload;
    }
  }, [pendingUpload]);

  // Sincronizar refs de file y plan
  useEffect(() => {
    fileRef.current = file;
    planRef.current = plan;
  }, [file, plan]);

  // Detectar errores desde props
  useEffect(() => {
    if (showModal && error) {
      setHasError(true);
      const msg = propErrorMessage || error.message || 'Error al procesar el archivo. No se pudieron actualizar los registros.';
      setErrorMessage(msg);
      notification.error(msg);
      setIsComplete(false);
      setCompletedWithoutProgress(false);
      setProgress(0);
      setCurrentRow(0);
    }
  }, [showModal, error, propErrorMessage]);

  // Conectar a StreamHub para recibir eventos en tiempo real
  useEffect(() => {
    if (!showModal) {
      // Reset cuando se cierra el modal
      setProgress(0);
      setCurrentRow(0);
      setSuccessCount(0);
      setErrorCount(0);
      setIsComplete(false);
      setCompletedWithoutProgress(false);
      setHasError(false);
      setErrorMessage(null);
      return;
    }

    // Si hay error desde props, no iniciar la conexión
    if (error) {
      return;
    }

    // Si no hay streamHubTopic o totalRows es 0, no iniciar la conexión
    if (!streamHubTopic || totalRows === 0) {
      return;
    }

    // Si no hay token, no iniciar la conexión
    if (!tokenRef.current) {
      setHasError(true);
      setErrorMessage('No se encontró el token de autenticación. Por favor, iniciá sesión nuevamente.');
      return;
    }

    // Reset estados
    setProgress(0);
    setCurrentRow(0);
    setSuccessCount(0);
    setErrorCount(0);
    setIsComplete(false);
    setCompletedWithoutProgress(false);
    setHasError(false);
    setErrorMessage(null);

    const STREAM_READY_TIMEOUT_MS = 15000;
    const PROGRESS_WATCHDOG_MS = 12000;

    let processedCount = 0;
    let eventSource = null;
    let streamReadyTimeout = null;
    let progressWatchdogTimer = null;
    let uploadStarted = false;
    let uploadFinishedOk = false;
    let streamErrorCount = 0;
    let streamReady = false;
    let enqueuedRowsLocal = totalRows;

    const closeEventSource = () => {
      if (streamReadyTimeout) {
        clearTimeout(streamReadyTimeout);
        streamReadyTimeout = null;
      }
      if (progressWatchdogTimer) {
        clearTimeout(progressWatchdogTimer);
        progressWatchdogTimer = null;
      }
      if (eventSource) {
        eventSource.close();
      }
    };

    const expectedRows = () =>
      enqueuedRowsLocal > 0 ? enqueuedRowsLocal : totalRows;

    // Enqueue OK but no SSE events — do not invent successCount / "N actualizados"
    const completeWithoutSseEvents = () => {
      if (processedCount > 0) {
        return;
      }
      setCompletedWithoutProgress(true);
      setSuccessCount(0);
      setProgress(100);
      setIsComplete(true);
      notification.info(
        'La actualización se envió. No recibimos el progreso en tiempo real; la lista se actualiza en segundo plano.',
      );
      // Refresh list only — onSuccess would close the modal and hide this screen
      if (typeof onRefreshRef.current === 'function') {
        onRefreshRef.current();
      }
      closeEventSource();
    };

    const armProgressWatchdog = () => {
      if (progressWatchdogTimer) {
        clearTimeout(progressWatchdogTimer);
      }
      progressWatchdogTimer = setTimeout(() => {
        if (uploadFinishedOk && processedCount === 0) {
          completeWithoutSseEvents();
        }
      }, PROGRESS_WATCHDOG_MS);
    };

    const startPendingUpload = async () => {
      if (uploadStarted) {
        return;
      }
      if (!isUploadPendingRef.current || !fileRef.current || !planRef.current) {
        return;
      }

      uploadStarted = true;
      isUploadPendingRef.current = false;
      setIsUploadPending(false);

      try {
        const response = await fetchResourceRP(() =>
          uploadBulkUpdate(fileRef.current, tokenRef.current, planRef.current.planId),
        );

        const responseStatus = response?.status;
        const responseData = response?.data;
        const hasSuccessStatus = responseStatus === 200 || responseStatus === 201;

        if (!hasSuccessStatus || !responseData || responseData.format_errors > 0) {
          const errorMsg =
            responseData?.errors?.[0]?.message ||
            'Error al procesar el archivo. No se pudieron actualizar los registros.';
          setHasError(true);
          setErrorMessage(errorMsg);
          notification.error(BackendRpErrorResponse.firstErrorMessage(errorMsg));
          closeEventSource();
          return;
        }

        const enqueued =
          responseData.enqueued_rows ??
          responseData.enqueuedRows ??
          responseData.data?.enqueued_rows ??
          responseData.data?.enqueuedRows;
        if (typeof enqueued === 'number' && enqueued >= 0) {
          enqueuedRowsLocal = enqueued;
        }

        uploadFinishedOk = true;
        armProgressWatchdog();
      } catch (uploadError) {
        console.error('Error uploading file:', uploadError);
        const msg = 'Error al subir el archivo. Por favor, intentá nuevamente.';
        notification.error(msg);
        setHasError(true);
        setErrorMessage(msg);
        closeEventSource();
      }
    };

    const markStreamReadyAndUpload = () => {
      if (streamReady) {
        return;
      }
      streamReady = true;
      streamErrorCount = 0;
      setIsConnectionReady(true);
      if (streamReadyTimeout) {
        clearTimeout(streamReadyTimeout);
        streamReadyTimeout = null;
      }
      // Backend emits stream_ready only after StreamHub is connected — upload immediately
      startPendingUpload();
    };

    try {
      const trimmedToken =
        typeof tokenRef.current === 'string' ? tokenRef.current.trim() : '';
      // StreamHub / dash guard expect raw JWT in query (not "Bearer …")
      const rawToken = trimmedToken.replace(/^Bearer\s+/i, '').trim();
      const normalizedTenant = tenantId ? String(tenantId).trim() : '';

      if (!rawToken) {
        setHasError(true);
        setErrorMessage('No se encontró el token de autenticación. Por favor, iniciá sesión nuevamente.');
        return;
      }

      const params = new URLSearchParams({
        topic: streamHubTopic,
        token: rawToken,
        clientId: normalizedTenant,
      });

      // Prefer direct Nest (REACT_APP_SSE_BACKEND_URL). If missing, same-origin /api/
      // so we do not hard-fail (nginx must have buffering off on stream locations).
      const effectiveUrl = (sseBackendUrl || '').replace(/\/$/, '');
      const streamPath = `/api/v1/recurring-payments/stream?${params.toString()}`;
      const url = effectiveUrl ? `${effectiveUrl}${streamPath}` : streamPath;

      eventSource = new EventSource(url, { withCredentials: true });

      const handleSubscriptionUpdate = (evt) => {
        try {
          const data = JSON.parse(evt.data);

          if (data.type === 'subscription_update') {
            processedCount++;
            streamErrorCount = 0;
            if (progressWatchdogTimer) {
              clearTimeout(progressWatchdogTimer);
              progressWatchdogTimer = null;
            }

            if (data.success) {
              setSuccessCount((prev) => prev + 1);
            } else {
              setErrorCount((prev) => prev + 1);
              setUpdateErrors((prev) => [
                ...prev,
                {
                  rowNumber: data.rowNumber,
                  subscriptionId: data.subscriptionId,
                  error: data.error,
                  errorCode: data.errorCode,
                },
              ]);
            }

            const rows = expectedRows();
            const percentage = rows > 0 ? (processedCount / rows) * 100 : 0;
            setProgress(Math.min(percentage, 100));
            setCurrentRow(processedCount);

            if (processedCount >= rows) {
              setIsComplete(true);
              closeEventSource();
            }
          }
        } catch (parseError) {
          console.error('Error parsing StreamHub event:', parseError);
        }
      };

      eventSource.addEventListener('subscription_update_success', handleSubscriptionUpdate);
      eventSource.addEventListener('subscription_update_error', handleSubscriptionUpdate);
      eventSource.addEventListener('stream_ready', () => {
        markStreamReadyAndUpload();
      });
      eventSource.addEventListener('stream_error', (evt) => {
        console.error('[BulkUpdate] stream_error', evt?.data);
        if (uploadFinishedOk && processedCount === 0) {
          completeWithoutSseEvents();
          return;
        }
        if (!uploadFinishedOk) {
          setHasError(true);
          const msg = 'Error al conectar con el servidor. Por favor, intentá nuevamente.';
          setErrorMessage(msg);
          notification.error(msg);
          closeEventSource();
        }
      });

      eventSource.onmessage = (event) => {
        handleSubscriptionUpdate(event);
      };

      eventSource.onopen = () => {
        streamErrorCount = 0;
      };

      // Only stream_ready starts the upload. Timeout = hard error (no blind upload).
      streamReadyTimeout = setTimeout(() => {
        if (!streamReady) {
          setHasError(true);
          const msg =
            'No se recibió confirmación del stream de progreso. Por favor, intentá nuevamente.';
          setErrorMessage(msg);
          notification.error(msg);
          closeEventSource();
        }
      }, STREAM_READY_TIMEOUT_MS);

      eventSource.onerror = (eventError) => {
        streamErrorCount += 1;
        console.error('StreamHub connection error:', eventError, {
          readyState: eventSource?.readyState,
          streamErrorCount,
          streamReady,
          uploadFinishedOk,
        });

        const isReconnecting = eventSource?.readyState === EventSource.CONNECTING;
        if (isReconnecting && streamErrorCount < 3) {
          return;
        }

        if (processedCount > 0) {
          closeEventSource();
          return;
        }

        if (uploadFinishedOk) {
          completeWithoutSseEvents();
          return;
        }

        if (!streamReady && isReconnecting) {
          return;
        }

        setHasError(true);
        const msg = 'Error al conectar con el servidor. Por favor, intentá nuevamente.';
        setErrorMessage(msg);
        notification.error(msg);
        closeEventSource();
      };
    } catch (connectionError) {
      console.error('Error creating EventSource:', connectionError);
      setHasError(true);
      const msg = 'Error al establecer conexión. Por favor, intentá nuevamente.';
      setErrorMessage(msg);
      notification.error(msg);
    }

    return () => {
      closeEventSource();
    };
  }, [showModal, streamHubTopic, totalRows, error]);

  const handleClose = () => {
    if (isComplete || hasError) {
      if (isComplete && onSuccess) {
        // Soft-complete already refreshed via onRefresh; still close via onSuccess
        onSuccess();
      }
      hideModal();
    }
  };

  if (!showModal) {
    return null;
  }

  return (
    <>
    <Modal 
      isOpen={showModal} 
      toggle={handleClose}
      centered
      backdrop={hasError ? true : 'static'}
      keyboard={hasError}
      className="bulk-update-progress-modal"
    >
      <ModalBody className="text-center py-5">
        {!isComplete && !hasError && (
          <>
            <div style={{ position: 'relative', height: '80px', marginBottom: '30px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Loader isLoading={true} absolute={false} />
            </div>
            <h5 className="modal-title mb-2">
              Actualizando {totalRows} registros...
            </h5>
            <p className="text-muted mb-4">
              Por favor, aguardá unos segundos...
            </p>
            <div className="progress mt-3" style={{ height: '8px' }}>
              <div 
                className="progress-bar progress-bar-striped progress-bar-animated" 
                role="progressbar" 
                style={{ width: `${progress}%` }}
                aria-valuenow={progress} 
                aria-valuemin="0" 
                aria-valuemax="100"
              />
            </div>
          </>
        )}

        {isComplete && !hasError && (
          <>
            <div style={{ 
              width: '80px', 
              height: '80px', 
              borderRadius: '50%', 
              backgroundColor: completedWithoutProgress
                ? '#fff3cd'
                : errorCount > 0
                  ? '#fff3cd'
                  : '#d4edda',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 30px'
            }}>
              <i
                className={`mdi ${
                  completedWithoutProgress || errorCount > 0
                    ? 'mdi-alert'
                    : 'mdi-check-circle'
                } mdi-48px`}
                style={{
                  color:
                    completedWithoutProgress || errorCount > 0 ? '#856404' : '#28a745',
                }}
              />
            </div>
            <h5 className="modal-title mb-3">
              {completedWithoutProgress
                ? 'Actualización enviada'
                : errorCount > 0
                  ? 'Actualización completada con errores'
                  : __('dashboard.sections.recurringPayments.plans.bulkUpdateProgress.success.title')
              }
            </h5>
            <p className="text-muted mb-4">
              {completedWithoutProgress
                ? 'No recibimos el progreso en tiempo real. La lista ya se actualizó; podés cerrar.'
                : `${successCount} registro(s) actualizado(s) correctamente${
                    errorCount > 0 ? ` • ${errorCount} registro(s) con error` : ''
                  }`}
            </p>
            <div className="d-flex justify-content-center mt-4" style={{ gap: '10px' }}>
              {!completedWithoutProgress && errorCount > 0 && (
                <Button
                  color="light"
                  outline
                  onClick={() => setShowErrorsModal(true)}
                  className="d-flex align-items-center"
                >
                  Revisar
                </Button>
              )}
              <Button
                color="primary"
                onClick={() => {
                  if (onSuccess) {
                    onSuccess();
                  } else {
                    hideModal();
                  }
                }}
              >
                Cerrar
              </Button>
            </div>
          </>
        )}

        {hasError && (
          <>
            <div style={{ 
              width: '80px', 
              height: '80px', 
              borderRadius: '50%', 
              backgroundColor: '#f8d7da',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 30px'
            }}>
              <i className="mdi mdi-alert-circle mdi-48px" style={{ color: '#dc3545' }}></i>
            </div>
            <h5 className="modal-title mb-3">
              Error en la actualización
            </h5>
            <Alert color="danger" className="mb-4">
              <div style={{ color: '#dc3545' }}>
                Error al procesar el archivo. No se pudieron actualizar los registros.
              </div>
            </Alert>
            <div className="d-flex justify-content-end mt-4">
              <Button color="primary" onClick={() => {
                if (onGoBack) {
                  onGoBack();
                }
                hideModal();
              }}>
                Volver
              </Button>
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
    
    {/* Modal de errores */}
    <Modal 
      isOpen={showErrorsModal} 
      toggle={() => setShowErrorsModal(false)}
      size="lg"
      centered
    >
      <ModalHeader toggle={() => setShowErrorsModal(false)}>
        Errores en la actualización
      </ModalHeader>
      <ModalBody>
        <div className="py-4">
          {/* Estadísticas */}
          <div className="row mb-4">
            <div className="col">
              <div className="rounded p-3 h-100" style={{ border: '1px solid #dee2e6' }}>
                <small className="text-muted d-block mb-2">Total de registros</small>
                <h3 className="mb-0 font-weight-bold text-left" style={{ color: '#000' }}>{totalRows}</h3>
              </div>
            </div>
            <div className="col">
              <div className="rounded p-3 h-100" style={{ border: '1px solid #dee2e6' }}>
                <small className="text-muted d-block mb-2">Correctos</small>
                <h3 className="mb-0 font-weight-bold text-success text-left">{successCount}</h3>
              </div>
            </div>
            <div className="col">
              <div className="rounded p-3 h-100" style={{ border: '1px solid #dee2e6' }}>
                <small className="text-muted d-block mb-2">Con error</small>
                <h3 className="mb-0 font-weight-bold text-danger text-left">{errorCount}</h3>
              </div>
            </div>
          </div>
          
          {/* Badge de registros con error */}
          <div className="d-flex align-items-center mb-3">
            <span className="badge badge-danger">
              <i className="mdi mdi-alert-circle mr-1"></i>
              Registros con error ({errorCount})
            </span>
          </div>
          
          {/* Tabla de errores */}
          {updateErrors.length > 0 && (
            <div className="mb-4">
              <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <Table className="table-hover mb-0">
                  <thead>
                    <tr>
                      <th className="text-muted" style={{ width: '100px', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Línea</th>
                      <th className="text-muted" style={{ width: '180px', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Campo</th>
                      <th className="text-muted" style={{ fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {updateErrors.map((err, idx) => (
                      <tr key={idx}>
                        <td className="align-middle">
                          <span className="text-muted">{err.rowNumber}</span>
                        </td>
                        <td className="align-middle">
                          <code className="text-danger font-weight-bold">ID</code>
                        </td>
                        <td className="align-middle">{err.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
          
          {/* Mensaje de advertencia */}
          <Alert color="warning" className="mb-4">
            <p className="mb-2">
              Para continuar, corregí los errores que presenta el archivo y volvé a cargarlo para actualizar los montos del plan.
            </p>
            <p className="mb-0">
              <strong>Recordá no modificar los datos que ya están completos.</strong>
            </p>
          </Alert>
          
          {/* Botón */}
          <div className="d-flex justify-content-end">
            <Button 
              color="light" 
              outline 
              onClick={() => {
                setShowErrorsModal(false);
                hideModal();
                // Llamar a onGoBack para que abra el modal de actualización
                if (onGoBack) {
                  onGoBack();
                }
              }}
            >
              Volver a cargar
            </Button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  </>
  );
};

